// Library methods
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// MUI Components
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";

// Components
import {
  Auth0Icon,
  CancelIcon,
  PlusIcon,
  SearchIcon,
  TrashIcon,
  UploadIcon,
} from "../../../assets/icons";
import ActionableSearchBar from "../ActionableSearchBar";

// Styles
import {
  CustomIconButtonDark,
  CustomIconButtonGray,
} from "../../../styles/muiStylesHelper";

const PageIntro = ({
  desktopMarginTop = 5,
  desktopMarginBottom = -2,
  pageTitle = "",
  pageTitleAdditionalComponents = "",
  bulkUploadButtonText = "",
  addButtonOnClick = () => {},
  deleteButtonOnClick = () => {},
  bulkUploadButtonOnClick = () => {},
  auth0ButtonOnClick = () => {},
  onFilterChange = () => {},
  clearSearchFilter = () => {},
  selectedRows = [],
  showAddButton = true,
  showDeleteButton = true,
  showBulkUploadButton = false,
  showFilterInput = true,
  showBreadcrumb = false,
  BreadcrumbComponent = "",
  disableSearchButton = false,
  disableDeleteButton,
  showAuth0Button = false,
  showIcons = true,
}) => {
  // internationalization
  const { t } = useTranslation();

  const inputRef = useRef(null);

  const [expandSearchInput, setExpandSearchInput] = useState(false);

  // hides the expanded search input when search button gets disabled
  // search button is usually disabled when there is no data to search, or, when data is being fetched
  useEffect(() => {
    if (!disableSearchButton) return;
    setExpandSearchInput(false);
  }, [disableSearchButton]);

  const MobileContent = () => (
    <Box
      display={{ xs: "flex", sm: "none" }}
      justifyContent={"space-between"}
      alignItems={"center"}
      marginTop={{ xs: 2 }}
    >
      <Box
        display={{ xs: "flex", sm: "none" }}
        alignItems={"center"}
        marginTop={{ xs: 2 }}
      >
        <Typography
          component="div"
          variant="h7"
          sx={{
            fontWeight: "bold",
            fontSize: { sm: "16px", lg: "20px" },
          }}
          textOverflow={"ellipsis"}
          overflow={"hidden"}
          whiteSpace={"nowrap"}
        >
          {pageTitle}
        </Typography>
        {pageTitleAdditionalComponents}
      </Box>

      <Grid
        item
        display={{ xs: "flex", sm: "none" }}
        justifyContent="right"
        gap="8px"
        sm={6}
        xs={12}
      >
        {showFilterInput && (
          <>
            {expandSearchInput ? (
              <TextField
                placeholder="Search..."
                id="search"
                inputRef={inputRef}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  style: {
                    backgroundColor: "#EFEFEF",
                    color: "#202338",
                    background: "#EFEFEF",
                    height: 40,
                    width: 150,
                    border: "1px solid #282C3C",
                    borderRadius: "4px",
                    fontSize: 16,
                  },
                  startAdornment: (
                    <Box sx={{ display: "flex", alignItems: "center", mx: 1 }}>
                      <SearchIcon />
                    </Box>
                  ),
                  endAdornment: (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mx: 1,
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        clearSearchFilter();
                        if (inputRef.current) {
                          inputRef.current.value = "";
                        }
                        setExpandSearchInput(false);
                      }}
                    >
                      <CancelIcon />
                    </Box>
                  ),
                }}
                onChange={onFilterChange}
                sx={{ width: { sm: 150, md: 150, lg: 250 }, mb: 0 }}
                data-testid="search-box-page-intro"
              />
            ) : null}

            {!expandSearchInput && (
              <IconButton
                aria-label="search"
                size="medium"
                sx={CustomIconButtonDark}
                disabled={disableSearchButton}
                onClick={() => {
                  // if clicking this button when filter input is showing up, clear the filter
                  if (expandSearchInput) clearSearchFilter();
                  setExpandSearchInput((prev) => !prev);
                }}
              >
                <SearchIcon />
              </IconButton>
            )}
          </>
        )}

        {showAddButton && (
          <IconButton
            aria-label="add"
            sx={CustomIconButtonDark}
            onClick={addButtonOnClick}
          >
            <PlusIcon />
          </IconButton>
        )}

        {Boolean(showAuth0Button) && (
          <IconButton
            aria-label="auth0-icon"
            sx={CustomIconButtonDark}
            onClick={auth0ButtonOnClick}
          >
            <Auth0Icon />
          </IconButton>
        )}

        {selectedRows.length > 0 && showDeleteButton && (
          <Tooltip
            title={t("exmas_with_device_delete_error")}
            disableHoverListener={!disableDeleteButton}
          >
            <span>
              <IconButton
                aria-label="search"
                sx={CustomIconButtonGray}
                disabled={disableSearchButton || disableDeleteButton}
                onClick={deleteButtonOnClick}
              >
                <TrashIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Grid>
    </Box>
  );

  const DesktopContent = () => (
    <Box
      display={{ xs: "none", sm: "flex" }}
      justifyContent="space-between"
      alignItems="center"
    >
      <Box
        width={
          !disableDeleteButton ? "82%" : !disableSearchButton ? "88%" : "95%"
        }
        display="flex"
        justifyContent="left"
        alignItems="center"
      >
        <Box display="flex" justifyContent="left" alignItems="center" gap={1}>
          <Typography
            component="div"
            variant="h7"
            sx={{
              fontWeight: "bold",
              fontSize: { sm: "16px", lg: "20px" },
            }}
            textOverflow={"ellipsis"}
            overflow={"hidden"}
            whiteSpace={"nowrap"}
          >
            {pageTitle}
          </Typography>
          {pageTitleAdditionalComponents}
        </Box>
      </Box>
      <Grid item display="flex" gap="8px" xs={6} justifyContent="right">
        {showIcons && (
          <>
            {
              <ActionableSearchBar
                placeholder={`${t("word_search")}...`}
                onSearchChange={onFilterChange}
                onClear={clearSearchFilter}
                showDeleteButton={showDeleteButton}
                disableDeleteButton={disableDeleteButton}
                deleteButtonOnClick={deleteButtonOnClick}
                showAuth0Button={Boolean(showAuth0Button)}
                auth0ButtonOnClick={auth0ButtonOnClick}
                showAddButton={showAddButton}
                addButtonOnClick={addButtonOnClick}
                selectedRows={selectedRows}
                showFilterInput={showFilterInput}
                addTooltip={t("patients_create_patient")}
                searchTooltip={t("word_search_patients")}
              />
            }
          </>
        )}

        {showBulkUploadButton && (
          <Tooltip title={bulkUploadButtonText}>
            <IconButton
              variant="outlined"
              sx={CustomIconButtonDark}
              onClick={bulkUploadButtonOnClick}
            >
              <UploadIcon />
            </IconButton>
          </Tooltip>
        )}
      </Grid>
    </Box>
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      mb={{ xs: 2, sm: desktopMarginBottom }}
      mt={{ xs: 4, sm: desktopMarginTop }}
    >
      {showBreadcrumb && BreadcrumbComponent}
      {DesktopContent()}
      {MobileContent()}
    </Box>
  );
};

export default PageIntro;
